'use client';

import { useEffect, useState, ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { AccessCodeModal } from '@/components/access-code-modal';

/**
 * 哪些路径不需要 access code 校验(公开访客可访问)
 * - /upload-mobile: 移动端扫码上传页(由 PC 登录用户主动发起扫码, mobile 端不需登录)
 */
const PUBLIC_PATHS = ['/upload-mobile'];

function isPublicPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function AccessCodeGuard({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<{
    enabled: boolean;
    authenticated: boolean;
    loading: boolean;
  }>({ enabled: false, authenticated: false, loading: true });
  const pathname = usePathname();
  const isPublic = isPublicPath(pathname);

  useEffect(() => {
    // 【2026-06-08 老板反馈】公开路径(例如 /upload-mobile)不走 access code 校验
    // 原因: PC 端已登录用户扫码发起, mobile 端是来宾, 不能要求来宾输 access code
    if (isPublic) {
      setStatus({ enabled: false, authenticated: false, loading: false });
      return;
    }
    let cancelled = false;
    fetch('/api/access-code/status')
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) {
          setStatus({
            enabled: data.enabled,
            authenticated: data.authenticated,
            loading: false,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Default to disabled on error — static deployments have no API server
          setStatus({ enabled: false, authenticated: false, loading: false });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isPublic]);

  const needsAuth = !isPublic && !status.loading && status.enabled && !status.authenticated;

  return (
    <>
      {needsAuth && (
        <AccessCodeModal
          open={true}
          onSuccess={() => setStatus((s) => ({ ...s, authenticated: true }))}
        />
      )}
      {children}
    </>
  );
}
